import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { YesNoRow } from "../ui/yes-no-toggle";
import { Building, AlertTriangle } from "lucide-react";

const BankAffiliatedPersonsSection = ({ data, onChange }) => {
  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleCheckboxChange = (field, checked) => {
    onChange({ [field]: checked });
    
    // Reset câmpuri dependente când se debifează
    if (field === 'isBankFamilyMember' && !checked) {
      onChange({ 
        isBankFamilyMember: false,
        bankName: "",
        employeeName: ""
      });
    }
  };

  const banks = [
    "BCR",
    "BRD", 
    "UniCredit Bank",
    "Exim Bank",
    "Raiffeisen Bank",
    "Banca Transilvania",
    "Garanti Bank",
    "Patria Credit",
    "TBI Bank",
    "ING Bank"
  ];

  return (
    <Card className="mb-6">
      <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b">
        <CardTitle className="flex items-center gap-2 text-orange-900">
          <Building className="h-5 w-5" />
          Declarație Persoane Afiliate Băncii
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {/* Textul legal explicativ */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold mb-2">Definiția părților afiliate instituției de credit:</p>
              <p className="text-xs leading-relaxed">
                Părțile afiliate instituției de credit cuprinde cel puțin: a) orice entitate asupra căreia instituția de credit exercită controlul; 
                b) orice entitate în care instituția de credit deține participații; c) entitățile care exercită control asupra instituției de credit; 
                d) orice entitate în care entitățile menționate la lit. c) fie exercită controlul, fie dețin participații; 
                e) acționarii care au dețineri calificate la capitalul instituției de credit; f) orice entitate în care acționarii menționați la lit. e) 
                fie exercită controlul, fie dețin participații; g) membrii organului de conducere al instituției de credit, precum și persoanele care 
                dețin funcții-cheie în respectiva instituție de credit, împreună cu entitățile în care aceștia au/prezintă interese directe sau indirecte 
                și membrii apropiați ai familiei acestora, care se anticipează să influențeze sau să fie influențați de aceștia în raport cu instituția 
                de credit; aceștia pot include: partenerul de viață și copiii persoanei; copiii partenerului de viață al persoanei; dependenți ai persoanei 
                sau ai partenerului de viață al acestuia.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Întrebarea principală */}
          <div className="space-y-4">
            <YesNoRow
              label="Sunteți membru al familiei unui salariat al vreunei Bănci?"
              value={data.isBankFamilyMember ?? null}
              onChange={(v) => handleCheckboxChange('isBankFamilyMember', v)}
              testid="bank-family-member"
            />

            {/* Câmpuri condiționale pentru bancă și angajat */}
            {data.isBankFamilyMember && (
              <div className="ml-6 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bankName">Selectați Banca *</Label>
                    <Select 
                      value={data.bankName || ""}
                      onValueChange={(value) => handleInputChange('bankName', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selectați banca" />
                      </SelectTrigger>
                      <SelectContent>
                        {banks.map((bank) => (
                          <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="employeeName">Numele Angajatului *</Label>
                    <Input
                      id="employeeName"
                      value={data.employeeName || ""}
                      onChange={(e) => handleInputChange('employeeName', e.target.value)}
                      placeholder="Introduceți numele angajatului"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Relații de grup */}
          <div className="space-y-4">
            <YesNoRow
              label="Relații de Grup?"
              value={data.hasGroupRelations ?? null}
              onChange={(v) => handleCheckboxChange('hasGroupRelations', v)}
              testid="group-relations"
            />
            
            {data.hasGroupRelations && (
              <div className="ml-6 p-4 bg-green-50 border border-green-200 rounded-lg space-y-4">
                <p className="text-sm text-green-800 mb-3">
                  Aveți relații de grup cu instituția financiară la care solicitați creditul.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="groupRelationBank">Selectați Banca *</Label>
                  <Select 
                    value={data.groupRelationBank || ""}
                    onValueChange={(value) => handleInputChange('groupRelationBank', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selectați banca pentru relațiile de grup" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Persoană afiliată */}
          <div className="space-y-4">
            <YesNoRow
              label="Persoană afiliată cu Banca?"
              value={data.isAffiliatedPerson ?? null}
              onChange={(v) => handleCheckboxChange('isAffiliatedPerson', v)}
              testid="affiliated-person"
            />
            
            {data.isAffiliatedPerson && (
              <div className="ml-6 p-4 bg-red-50 border border-red-200 rounded-lg space-y-4">
                <p className="text-sm text-red-800 mb-3">
                  Sunteți considerată persoană afiliată cu instituția bancară conform definițiilor legale.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="affiliatedBank">Selectați Banca *</Label>
                  <Select 
                    value={data.affiliatedBank || ""}
                    onValueChange={(value) => handleInputChange('affiliatedBank', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selectați banca cu care sunteți afiliat" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Notă finală */}
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>Notă:</strong> Aceste informații sunt necesare pentru evaluarea corectă a cererii de credit 
              și respectarea reglementărilor bancare în vigoare. Toate datele furnizate vor fi tratate confidențial 
              și vor fi folosite exclusiv în scopurile menționate.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BankAffiliatedPersonsSection;